import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

const hotVideos = videos.filter((v) => v.isHot).slice(0, 6);
const newVideos = videos.filter((v) => v.isNew).slice(0, 6);

export default function Home() {
  const [heroSearch, setHeroSearch] = useState('');

  const handleHeroSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (heroSearch.trim()) {
      setHeroSearch('');
    }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '向日葵视频',
    url: 'https://www.xiangrikuishipin.com/',
    description: '向日葵视频提供91视频、97视频等数字视频平台的高清免费在线观看，覆盖51视频、久久视频等数字视频大全。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.xiangrikuishipin.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '向日葵视频',
    url: 'https://www.xiangrikuishipin.com/',
    description: '向日葵视频是国内领先的数字视频在线观看平台，提供91视频、97视频、51视频、久久视频等高清数字视频大全。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座15层',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      postalCode: '100022',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-6688',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '向日葵视频是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，向日葵视频平台上的所有视频内容均免费提供在线观看，您无需支付任何费用即可畅享海量高清数字视频资源。',
        },
      },
      {
        '@type': 'Question',
        name: '91视频和97视频有什么区别？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '91视频频道侧重于热门影视和动漫内容，97视频频道则更注重经典影片和美食旅行类内容。两个频道各有特色，您可以根据自己的喜好选择观看。',
        },
      },
      {
        '@type': 'Question',
        name: '平台支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '向日葵视频全面支持PC端、手机端和平板端观看。您可以通过浏览器直接访问，也可以下载移动客户端，享受更便捷的观看体验。支持iOS和Android系统。',
        },
      },
      {
        '@type': 'Question',
        name: '视频画质如何？支持高清播放吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '向日葵视频平台上的大部分内容均支持高清（1080P）播放，部分精选内容支持超高清（4K）播放。系统会根据您的网络状况自动调整最佳画质。',
        },
      },
      {
        '@type': 'Question',
        name: '如何搜索我想看的视频？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以在页面顶部的搜索框中输入关键词进行搜索，也可以通过分类导航浏览不同频道的视频内容。智能推荐系统还会根据您的观看历史推荐可能感兴趣的视频。',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20sunflower%20field%20at%20sunset%20with%20soft%20bokeh%20effect%2C%20dreamy%20atmospheric%20lighting%2C%20artistic%20photography%20style%2C%20elegant%20golden%20and%20amber%20tones%2C%20cinematic%20wide%20angle%20composition%2C%20ethereal%20glow&width=1600&height=900&seq=hero-home-2026&orientation=landscape"
            alt="向日葵视频 - 数字视频大全平台"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
          <div className="relative z-10 h-full flex flex-col items-center justify-center w-full px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-4 leading-tight">
              向日葵视频 - 数字视频大全
            </h1>
            <p className="text-background-200/90 text-sm md:text-lg max-w-2xl mb-8 leading-relaxed">
              向日葵视频提供<strong>91视频</strong>、<strong>97视频</strong>等数字视频平台的高清免费在线观看，
              覆盖<strong>51视频</strong>、<strong>久久视频</strong>等数字视频大全，同类型数字视频在线流畅体验。
            </p>
            <form onSubmit={handleHeroSearch} className="w-full max-w-lg flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={heroSearch}
                onChange={(e) => setHeroSearch(e.target.value)}
                placeholder="搜索你喜欢的视频内容..."
                className="flex-1 px-5 py-3 rounded-xl text-sm bg-background-50/95 text-foreground-900 placeholder-foreground-400 border border-transparent outline-none focus:ring-2 focus:ring-primary-400"
              />
              <button
                type="submit"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索视频
              </button>
            </form>
            <div className="flex items-center gap-4 mt-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="text-background-50/80 hover:text-background-50 text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                探索视频分类
              </h2>
              <p className="text-foreground-500 text-sm max-w-xl mx-auto">
                五大精选视频频道，覆盖热门影视、经典佳作、纪录片、综艺娱乐和科技数码，满足您的多样化观影需求
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
                >
                  <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-secondary-100 group-hover:bg-primary-100 transition-colors mb-3">
                    <i className={`${cat.icon} text-xl md:text-2xl text-secondary-600 group-hover:text-primary-600 transition-colors`}></i>
                  </span>
                  <span className="font-heading text-foreground-900 font-semibold text-sm md:text-base whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-foreground-400 text-xs mt-1">
                    {cat.slug === '91-video' ? '热门影视' : cat.slug === '97-video' ? '经典佳作' : cat.slug === '51-video' ? '纪录片' : cat.slug === 'jiujiu-video' ? '综艺娱乐' : '科技数码'}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Videos */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  <i className="ri-fire-line text-accent-500 mr-2"></i>
                  热门推荐
                </h2>
                <p className="text-foreground-500 text-sm">
                  最受用户欢迎的视频内容，大家都在看
                </p>
              </div>
              <Link
                to="/category/91-video"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {hotVideos.map((video) => (
                <VideoCard key={video.id} video={video} showCategory />
              ))}
            </div>
            <div className="mt-6 text-center sm:hidden">
              <Link
                to="/category/91-video"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                查看更多热门视频 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Videos */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  <i className="ri-timer-flash-line text-primary-500 mr-2"></i>
                  最新上线
                </h2>
                <p className="text-foreground-500 text-sm">
                  每日更新，精彩内容不错过
                </p>
              </div>
              <Link
                to="/category/digital-video"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {newVideos.map((video) => (
                <VideoCard key={video.id} video={video} showCategory />
              ))}
            </div>
            <div className="mt-6 text-center sm:hidden">
              <Link
                to="/category/digital-video"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                查看更多最新视频 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                为什么选择向日葵视频？
              </h2>
              <p className="text-foreground-500 text-sm max-w-xl mx-auto">
                我们致力于打造最好的数字视频在线观看平台
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-video-line', title: '海量资源', desc: '超过100万部视频资源，涵盖91视频、97视频、51视频、久久视频等多种类型' },
                { icon: 'ri-hd-line', title: '高清画质', desc: '支持1080P高清播放，部分内容支持4K超高清，自动适配最佳画质' },
                { icon: 'ri-device-line', title: '多端同步', desc: 'PC、手机、平板全平台支持，随时随地畅享数字视频在线体验' },
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '银行级数据加密，严格内容审核，保障您的隐私和观看安全' },
              ].map((feature, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-4">
                    <i className={`${feature.icon} text-xl text-primary-600`}></i>
                  </span>
                  <h3 className="font-heading text-foreground-900 font-semibold mb-2">{feature.title}</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm">
                关于向日葵视频的常见疑问解答
              </p>
            </div>
            <div className="space-y-3">
              {[
                {
                  q: '向日葵视频是免费的吗？',
                  a: '是的，向日葵视频平台上的所有视频内容均免费提供在线观看，您无需支付任何费用即可畅享海量高清数字视频资源。我们致力于为用户打造无障碍的数字视频在线体验。',
                },
                {
                  q: '91视频和97视频有什么区别？',
                  a: '91视频频道侧重于热门影视和动漫内容，97视频频道则更注重经典影片和美食旅行类内容。此外，51视频聚焦纪录片和体育，久久视频主打综艺和教育，数字视频大全涵盖科技、音乐和健身等多元内容。',
                },
                {
                  q: '平台支持哪些设备观看？',
                  a: '向日葵视频全面支持PC端、手机端和平板端观看。您可以通过浏览器直接访问我们的网站，享受便捷的数字视频在线观看体验。',
                },
                {
                  q: '视频画质如何？支持高清播放吗？',
                  a: '向日葵视频平台上的大部分内容均支持高清（1080P）播放，部分精选内容支持超高清（4K）播放。系统会根据您的网络状况自动调整最佳画质，您也可以手动选择画质。',
                },
                {
                  q: '如何搜索我想看的视频？',
                  a: '您可以在页面顶部的搜索框中输入关键词进行搜索，也可以通过分类导航浏览不同频道的视频内容。我们的智能推荐系统还会根据您的观看历史，为您推荐可能感兴趣的视频。',
                },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-foreground-900 text-sm">
                    <span>{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              立即开始您的观影之旅
            </h2>
            <p className="text-foreground-500 text-sm mb-8 max-w-lg mx-auto">
              加入向日葵视频，畅享91视频、97视频、51视频、久久视频等海量高清数字视频大全，免费在线观看，精彩不容错过！
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/91-video"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览91视频
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50 hover:bg-background-100 text-foreground-900 font-medium text-sm rounded-xl border border-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}